import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Video, Loader2, X, Calendar as CalendarIcon, Clock, MapPin, Send, Ban, Pencil, Sparkles, ShieldAlert, CircleDot, Upload, Download, FileVideo, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * ScheduleMeetingDialog — obsidian/gold modal that lets an eligible host
 * (Admin, Level-2 sponsor, or Seller with quoted buyers) pick a partner,
 * type an agenda, choose start-time + free-form duration, and fire off the
 * Jitsi meeting. Backend enforces the permission matrix.
 *
 * Props
 *  - api: axios instance
 *  - open: bool
 *  - onClose: () => void
 *  - onCreated: (meeting) => void
 *  - guestPreset: { id, business_name, email }  optional — locks the guest
 *    picker when the dialog is opened from a specific partner card.
 */
export function ScheduleMeetingDialog({ api, open, onClose, onCreated, guestPreset }) {
  const [invitees, setInvitees] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    guest_id: "", title: "", agenda: "",
    date: "", time: "", duration_minutes: 30,
  });

  useEffect(() => {
    if (!open) return;
    if (guestPreset?.id) {
      setForm((f) => ({ ...f, guest_id: guestPreset.id, title: `Sync with ${guestPreset.business_name || guestPreset.email}` }));
    }
    setInvLoading(true);
    api.get("/meetings/invitees")
      .then((r) => setInvitees(r.data?.invitees || []))
      .catch(() => setInvitees([]))
      .finally(() => setInvLoading(false));
  }, [open, api, guestPreset]);

  const submit = async () => {
    if (!form.guest_id) { toast.error("Pick a partner to invite"); return; }
    if (!form.title.trim() || form.title.trim().length < 2) { toast.error("Add a meeting title"); return; }
    if (!form.date || !form.time) { toast.error("Set a date and time"); return; }
    const start_at = new Date(`${form.date}T${form.time}`).toISOString();
    if (new Date(start_at) < new Date(Date.now() - 60_000)) { toast.error("Pick a future date & time"); return; }
    const dur = Number(form.duration_minutes);
    if (!Number.isFinite(dur) || dur < 5 || dur > 480) { toast.error("Duration must be between 5 and 480 minutes"); return; }
    setBusy(true);
    try {
      const r = await api.post("/meetings", {
        guest_id: form.guest_id, title: form.title.trim(),
        agenda: form.agenda.trim(), start_at, duration_minutes: dur,
      });
      toast.success("Meeting scheduled · invites emailed");
      onCreated?.(r.data);
      onClose?.();
      setForm({ guest_id: "", title: "", agenda: "", date: "", time: "", duration_minutes: 30 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule meeting");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85]" data-testid="schedule-meeting-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-[540px] bg-[#070707] border-l border-[#B87333]/30 overflow-y-auto">
        <div className="sticky top-0 bg-[#070707] px-6 py-4 border-b border-white/10 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk flex items-center gap-2">
              <Video size={12}/> Schedule Meeting
            </div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">Secure Jitsi call · unique link</div>
          </div>
          <button data-testid="schedule-meeting-close" onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0] p-1"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Guest picker */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Invite partner *</label>
            {guestPreset?.id ? (
              <div className="rounded-sm border border-[#B87333]/30 bg-[#B87333]/[0.05] px-3 py-2.5" data-testid="meeting-guest-locked">
                <div className="font-serif text-sm text-[#F5F5F0]">{guestPreset.business_name || guestPreset.email}</div>
                <div className="text-[10px] text-[#8A8A82] font-grotesk uppercase tracking-[0.18em]">{guestPreset.email}</div>
              </div>
            ) : invLoading ? (
              <div className="text-xs text-[#8A8A82]"><Loader2 size={12} className="inline animate-spin"/> Loading network…</div>
            ) : invitees.length === 0 ? (
              <div className="rounded-sm border border-[#C0392B]/30 bg-[#C0392B]/[0.05] px-3 py-2.5 text-xs text-[#e08b8f]" data-testid="meeting-no-invitees">
                You don't have any partners in your invitable network yet. Admins can invite anyone; Level-2 sponsors can invite their dependents; sellers can invite buyers they've quoted.
              </div>
            ) : (
              <select data-testid="meeting-guest-select" value={form.guest_id}
                      onChange={(e) => setForm((f) => ({ ...f, guest_id: e.target.value }))}
                      className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]">
                <option value="">— Choose a partner —</option>
                {invitees.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {(inv.business_name || inv.name || inv.email) + (inv.vendor_code ? ` · ${inv.vendor_code}` : "")}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Topic *</label>
            <input data-testid="meeting-title" value={form.title}
                   onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                   placeholder="Q2 quotation walkthrough"
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          </div>

          {/* Agenda */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Agenda</label>
            <textarea data-testid="meeting-agenda" value={form.agenda} rows={3}
                      onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))}
                      placeholder="Line items, delivery windows, credit terms…"
                      className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333] resize-none" />
          </div>

          {/* Date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Date *</label>
              <input data-testid="meeting-date" type="date" value={form.date}
                     onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            </div>
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Time *</label>
              <input data-testid="meeting-time" type="time" value={form.time}
                     onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Duration (minutes) *</label>
            <div className="flex items-center gap-2">
              <input data-testid="meeting-duration" type="number" min="5" max="480" value={form.duration_minutes}
                     onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                     className="w-32 bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
              <div className="flex flex-wrap gap-1.5" data-testid="meeting-duration-presets">
                {[15, 30, 45, 60].map((d) => (
                  <button key={d} type="button" onClick={() => setForm((f) => ({ ...f, duration_minutes: d }))}
                          className={`rounded-full px-3 py-1 text-[10px] font-grotesk border transition-colors ${
                            Number(form.duration_minutes) === d
                              ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10"
                              : "border-white/10 text-[#C9C9C2] hover:border-white/30"
                          }`}>{d} min</button>
                ))}
              </div>
            </div>
            <div className="text-[10px] text-[#8A8A82] mt-1">Free-form. Any value 5–480 minutes.</div>
          </div>

          <button data-testid="meeting-submit" onClick={submit} disabled={busy || invitees.length === 0 && !guestPreset?.id}
                  className="w-full rounded-full bg-[#B87333] text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Send Invite & Schedule
          </button>
          <p className="text-[10px] text-[#8A8A82] text-center leading-relaxed">
            A unique <span className="text-[#B87333]">Jitsi Meet</span> room is generated and emailed to both parties instantly.
          </p>
        </div>
      </div>
    </div>
  );
}


/**
 * MeetingsPanel — dashboard component. Lists the current user's upcoming
 * meetings with a live "starts in 8m" pill + one-click Join Now.
 */
export function MeetingsPanel({ api, canSchedule = true, embedded = false }) {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("upcoming");
  const [dialog, setDialog] = useState(false);
  const [rescheduling, setRescheduling] = useState(null); // meeting object
  const [tick, setTick] = useState(0);

  // Live pill refresh every 30 s
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // T-15m in-app toast — fires once per meeting per browser session when it
  // enters the 15-minute-to-start window so users never miss a verification.
  useEffect(() => {
    const now = Date.now();
    meetings.forEach((m) => {
      if (m.status === "cancelled") return;
      const start = m.start_at ? new Date(m.start_at).getTime() : 0;
      const diff = start - now;
      if (diff <= 0 || diff > 15 * 60_000) return;
      const key = `vinci-meeting-t15-toast-${m.id}`;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, "1");
      } catch { /* private mode fallback: allow re-firing */ }
      const mins = Math.max(1, Math.round(diff / 60_000));
      toast.info(`${m.title} starts in ${mins}m`, {
        description: "Powered by Jitsi · click Join Now on the meeting row.",
        duration: 8000,
      });
    });
  }, [meetings, tick]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/meetings/mine?scope=${scope}`);
      setMeetings(r.data?.meetings || []);
    } catch { setMeetings([]); }
    finally { setLoading(false); }
  }, [api, scope]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (m) => {
    const reason = window.prompt(`Cancel "${m.title}"?\n\nOptional note to the other party:`, "");
    if (reason === null) return;
    try {
      await api.post(`/meetings/${m.id}/cancel`, { reason: reason.trim() });
      toast.success("Meeting cancelled · notice emailed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <section className={embedded ? "" : "mt-14 pt-10 border-t border-white/10"} data-testid="meetings-panel">
      {!embedded && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[#B87333] text-black">
              <Video size={17} />
            </span>
            <h2 className="font-serif text-2xl md:text-3xl">Meetings</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-white/10 overflow-hidden" data-testid="meetings-scope-tabs">
              {["upcoming", "past"].map((s) => (
                <button key={s} onClick={() => setScope(s)} data-testid={`meetings-scope-${s}`}
                        className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.22em] transition-colors ${
                          scope === s ? "bg-[#B87333] text-black" : "text-[#8A8A82] hover:text-[#F5F5F0]"
                        }`}>{s}</button>
              ))}
            </div>
            {canSchedule && (
              <button data-testid="meetings-schedule-btn" onClick={() => setDialog(true)}
                      className="rounded-full bg-[#B87333] text-black px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 inline-flex items-center gap-2">
                <Sparkles size={12}/> Schedule
              </button>
            )}
          </div>
        </div>
      )}
      {!embedded && (
        <p className="font-body text-sm text-[#8A8A82] mt-1 mb-6 max-w-xl leading-relaxed">
          Video calls between Vinci partners. Every meeting spins up a browser-native <b className="text-[#F5F5F0]">Jitsi</b> room — no plugin, no app install. <span className="text-[#8A8A82]">Zoho Meeting & Google Meet available on request.</span>
        </p>
      )}
      {embedded && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex rounded-full border border-white/10 overflow-hidden" data-testid="meetings-scope-tabs">
            {["upcoming", "past"].map((s) => (
              <button key={s} onClick={() => setScope(s)} data-testid={`meetings-scope-${s}`}
                      className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.22em] transition-colors ${
                        scope === s ? "bg-[#B87333] text-black" : "text-[#8A8A82] hover:text-[#F5F5F0]"
                      }`}>{s}</button>
            ))}
          </div>
          {canSchedule && (
            <button data-testid="meetings-schedule-btn" onClick={() => setDialog(true)}
                    className="rounded-full bg-[#B87333] text-black px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 inline-flex items-center gap-2">
              <Sparkles size={12}/> Schedule
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#8A8A82] font-body"><Loader2 className="inline w-4 h-4 animate-spin mr-2"/> Loading meetings…</div>
      ) : meetings.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-sm p-8 text-center" data-testid="meetings-empty">
          <Video size={22} className="text-[#8A8A82] mx-auto mb-3" />
          <p className="font-serif text-lg text-[#F5F5F0]">No {scope} meetings.</p>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            {scope === "upcoming"
              ? (canSchedule ? "Schedule a call with a partner and it will appear here." : "Meetings scheduled with you will appear here.")
              : "Past meetings will be archived here after they end."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="meetings-list">
          {meetings.map((m) => (
            <MeetingRow key={m.id} m={m} tick={tick}
                        currentUser={user}
                        api={api}
                        onChange={load}
                        onCancel={cancel}
                        onReschedule={setRescheduling} />
          ))}
        </div>
      )}

      <ScheduleMeetingDialog api={api} open={dialog} onClose={() => setDialog(false)}
                               onCreated={() => load()} />
      {rescheduling && (
        <RescheduleDialog api={api} meeting={rescheduling}
                          onClose={() => setRescheduling(null)}
                          onDone={() => { setRescheduling(null); load(); }} />
      )}
    </section>
  );
}


/**
 * MeetingRow — single line item in the panel. Colour + copy shift based on
 * whether the meeting is imminent, in-progress, or finished.
 */
function MeetingRow({ m, tick, currentUser, api, onChange, onCancel, onReschedule }) {
  const now = Date.now();
  void tick; // re-render trigger only
  const start = m.start_at ? new Date(m.start_at).getTime() : 0;
  const end = start + (m.duration_minutes || 30) * 60_000;
  const diff = start - now;
  const [recPrompt, setRecPrompt] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef(null);

  const relPill = (() => {
    if (m.status === "cancelled") return { color: "#C0392B", label: "Cancelled" };
    if (now > end) return { color: "#8A8A82", label: "Ended" };
    if (now >= start) return { color: "#2FB999", label: "Live now" };
    if (diff <= 15 * 60_000) return { color: "#D8B23E", label: `Starts in ${Math.max(1, Math.round(diff / 60_000))}m` };
    if (diff <= 24 * 60 * 60_000) return { color: "#B87333", label: `Starts in ${Math.round(diff / 60 / 60_000)}h` };
    const days = Math.round(diff / (24 * 60 * 60_000));
    return { color: "#8A8A82", label: `In ${days}d` };
  })();

  const canJoin = m.status !== "cancelled" && diff <= 15 * 60_000 && now <= end;
  const isHost = currentUser && (currentUser.id === m.host_id || currentUser.role === "admin");
  const canManage = isHost && m.status !== "cancelled" && now < start;
  const isAdmin = currentUser?.role === "admin";
  const showRecordReminder = isAdmin && (m.provider === "jitsi" || !m.provider);
  const startPretty = new Date(m.start_at).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  const openJitsi = () => {
    window.open(m.join_url, "_blank", "noopener,noreferrer");
    setRecPrompt(false);
  };
  const handleAdminJoin = (e) => {
    if (!showRecordReminder) return;
    e.preventDefault();
    setRecPrompt(true);
  };

  const rec = m.recording || null;
  const meetingEnded = now > end;
  const showRecordingVault = isAdmin && m.provider !== "meet"; // Jitsi lane owns the archive
  const uploadRecording = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large — cap is 500 MB. Compress or split before uploading.");
      return;
    }
    setUploadBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/meetings/${m.id}/recording`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Recording archived · attached to partner KYC record.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const downloadRecording = async () => {
    try {
      const res = await api.get(`/meetings/${m.id}/recording`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rec?.filename || `vinci-meeting-${m.id}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download recording");
    }
  };
  const removeRecording = async () => {
    if (!window.confirm("Remove the archived recording? This cannot be undone.")) return;
    try {
      await api.delete(`/meetings/${m.id}/recording`);
      toast.success("Recording removed from archive");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove recording");
    }
  };
  const humanSize = (n) => {
    if (!n) return "—";
    if (n > 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.round(n / 1024)} KB`;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="border rounded-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        style={{ borderColor: canJoin ? "rgba(47,185,153,0.4)" : "rgba(255,255,255,0.08)",
                 background: canJoin ? "rgba(47,185,153,0.04)" : "transparent" }}
        data-testid={`meeting-row-${m.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border"
                style={{ color: relPill.color, borderColor: relPill.color + "60" }}
                data-testid={`meeting-pill-${m.id}`}>
            {relPill.label}
          </span>
          <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
            {m.provider === "jitsi" ? "Jitsi Meet" : m.provider}
          </span>
        </div>
        <div className="font-serif text-lg text-[#F5F5F0] mt-2">{m.title}</div>
        <div className="font-body text-xs text-[#8A8A82] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><CalendarIcon size={11}/> {startPretty}</span>
          <span className="inline-flex items-center gap-1"><Clock size={11}/> {m.duration_minutes} min</span>
          <span className="inline-flex items-center gap-1"><MapPin size={11}/> {m.host_name} ↔ {m.guest_name}</span>
        </div>
        {m.agenda && <div className="font-body text-xs text-[#C9C9C2] mt-2 whitespace-pre-line line-clamp-3">{m.agenda}</div>}
        {m.status === "cancelled" && m.cancelled_reason && (
          <div className="mt-2 text-[11px] text-[#e08b8f] font-body">Cancellation note: {m.cancelled_reason}</div>
        )}
        {showRecordReminder && m.status !== "cancelled" && now <= end && (
          <div
            data-testid={`meeting-record-reminder-${m.id}`}
            className="mt-3 inline-flex items-start gap-2 rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/[0.06] px-3 py-2"
          >
            <CircleDot size={12} className="text-[#C0392B] mt-0.5 shrink-0 animate-pulse" />
            <div className="min-w-0">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C0392B]">
                Admin · Record for Legal Archive
              </div>
              <div className="font-body text-[11px] text-[#C9C9C2] mt-0.5 leading-snug">
                Once inside Jitsi, open the <b className="text-[#F5F5F0]">⋮ More</b> menu and tap
                <b className="text-[#F5F5F0]"> Start recording</b> to capture the verification.
              </div>
            </div>
          </div>
        )}

        {showRecordingVault && (now >= start - 5 * 60_000 || meetingEnded || rec) && m.status !== "cancelled" && (
          <div
            data-testid={`meeting-recording-vault-${m.id}`}
            className={`mt-3 rounded-sm border px-4 py-3 ${
              rec ? "border-[#2FB999]/40 bg-[#2FB999]/[0.05]" : "border-[#D8B23E]/40 bg-[#D8B23E]/[0.05]"
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileVideo size={16} className={rec ? "text-[#2FB999]" : "text-[#D8B23E]"} />
                <div className="min-w-0">
                  <div className={`font-grotesk text-[10px] uppercase tracking-[0.22em] ${
                    rec ? "text-[#2FB999]" : "text-[#D8B23E]"
                  }`}>
                    {rec ? "Recording archived · Legal Vault" : "Awaiting recording upload"}
                  </div>
                  <div className="font-body text-[11px] text-[#C9C9C2] mt-0.5 truncate">
                    {rec
                      ? `${rec.filename} · ${humanSize(rec.size_bytes)} · uploaded ${new Date(rec.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                      : "Stop the Jitsi recording, save the file, then upload it here to attach to the partner's KYC record."}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,audio/*"
                  data-testid={`meeting-recording-file-${m.id}`}
                  className="hidden"
                  onChange={(e) => uploadRecording(e.target.files?.[0])}
                />
                <button
                  data-testid={`meeting-recording-upload-${m.id}`}
                  disabled={uploadBusy}
                  onClick={() => fileInputRef.current?.click()}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-grotesk transition-colors disabled:opacity-50 ${
                    rec
                      ? "border border-white/20 hover:border-[#D8B23E] text-[#C9C9C2] hover:text-[#D8B23E]"
                      : "bg-[#D8B23E] text-black hover:brightness-110"
                  }`}
                >
                  {uploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {rec ? "Replace" : "Upload recording"}
                </button>
                {rec && (
                  <>
                    <button
                      data-testid={`meeting-recording-download-${m.id}`}
                      onClick={downloadRecording}
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-[#2FB999] text-[#C9C9C2] hover:text-[#2FB999] px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-grotesk"
                    >
                      <Download size={12} /> Fetch
                    </button>
                    <button
                      data-testid={`meeting-recording-delete-${m.id}`}
                      onClick={removeRecording}
                      aria-label="Remove recording from archive"
                      title="Remove recording from archive"
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-[#C0392B] text-[#C9C9C2] hover:text-[#C0392B] px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-grotesk"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {canJoin ? (
          <a href={m.join_url} target="_blank" rel="noreferrer" data-testid={`meeting-join-${m.id}`}
             onClick={handleAdminJoin}
             className="rounded-full bg-[#2FB999] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.24em] hover:brightness-110 inline-flex items-center gap-2">
            <Video size={12}/> {showRecordReminder ? "Join & Record" : "Join Now"}
          </a>
        ) : m.status !== "cancelled" && now < start ? (
          <a href={m.join_url} target="_blank" rel="noreferrer" data-testid={`meeting-join-${m.id}`}
             onClick={handleAdminJoin}
             className="rounded-full border border-white/20 hover:border-[#B87333] text-[#C9C9C2] hover:text-[#B87333] px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-2">
            <Video size={12}/> Open link
          </a>
        ) : null}
        {canManage && (
          <>
            <button data-testid={`meeting-reschedule-${m.id}`} onClick={() => onReschedule(m)}
                    className="rounded-full border border-white/20 hover:border-[#D8B23E] text-[#C9C9C2] hover:text-[#D8B23E] px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-1.5">
              <Pencil size={11}/> Reschedule
            </button>
            <button data-testid={`meeting-cancel-${m.id}`} onClick={() => onCancel(m)}
                    className="rounded-full border border-white/20 hover:border-[#C0392B] text-[#C9C9C2] hover:text-[#C0392B] px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-1.5">
              <Ban size={11}/> Cancel
            </button>
          </>
        )}
      </div>

      <AnimatePresence>
        {recPrompt && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setRecPrompt(false)}
            data-testid={`meeting-record-modal-${m.id}`}
          >
            <motion.div
              initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-md w-full rounded-sm border border-[#C0392B]/40 bg-[#0a0a0a] p-7 shadow-[0_30px_120px_rgba(192,57,43,0.25)]"
            >
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#C0392B]/15 text-[#C0392B] shrink-0">
                  <ShieldAlert size={18} />
                </span>
                <div className="min-w-0">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#C0392B]">
                    Legal Archive · Recording Reminder
                  </div>
                  <div className="font-serif text-2xl text-[#F5F5F0] mt-1">Start Jitsi recording once inside.</div>
                  <p className="font-body text-sm text-[#C9C9C2] mt-3 leading-relaxed">
                    Open the <b className="text-[#F5F5F0]">⋮ More options</b> menu in the Jitsi toolbar and select
                    <b className="text-[#F5F5F0]"> Start recording</b>. Save the file to the Vinci legal archive at
                    the end of the session for compliance retention.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button
                  data-testid={`meeting-record-cancel-${m.id}`}
                  onClick={() => setRecPrompt(false)}
                  className="rounded-full border border-white/20 hover:border-white/40 text-[#C9C9C2] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]"
                >
                  Not yet
                </button>
                <button
                  data-testid={`meeting-record-confirm-${m.id}`}
                  onClick={openJitsi}
                  className="rounded-full bg-[#C0392B] text-white px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:brightness-110 inline-flex items-center gap-2"
                >
                  <CircleDot size={12} className="animate-pulse" /> Understood — Join & Record
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


function RescheduleDialog({ api, meeting, onClose, onDone }) {
  const iso = meeting.start_at ? new Date(meeting.start_at) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(`${iso.getFullYear()}-${pad(iso.getMonth() + 1)}-${pad(iso.getDate())}`);
  const [time, setTime] = useState(`${pad(iso.getHours())}:${pad(iso.getMinutes())}`);
  const [duration, setDuration] = useState(meeting.duration_minutes || 30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!date || !time) return;
    const start_at = new Date(`${date}T${time}`).toISOString();
    setBusy(true);
    try {
      await api.post(`/meetings/${meeting.id}/reschedule`, {
        start_at, duration_minutes: Number(duration), reason: reason.trim(),
      });
      toast.success("Meeting rescheduled · notice emailed");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90]" data-testid="reschedule-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[480px] bg-[#070707] border border-[#B87333]/30 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] font-grotesk">Reschedule Meeting</div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">{meeting.title}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Date *</label>
            <input data-testid="reschedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          </div>
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Time *</label>
            <input data-testid="reschedule-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          </div>
        </div>
        <div className="mb-3">
          <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Duration (min)</label>
          <input data-testid="reschedule-duration" type="number" min="5" max="480" value={duration} onChange={(e) => setDuration(e.target.value)}
                 className="w-32 bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
        </div>
        <div className="mb-4">
          <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Note to the other party</label>
          <textarea data-testid="reschedule-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Optional — 'Need to move due to travel'"
                    className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333] resize-none" />
        </div>
        <button data-testid="reschedule-submit" onClick={submit} disabled={busy}
                className="w-full rounded-full bg-[#D8B23E] text-black px-6 py-2.5 text-[11px] uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Pencil size={14}/>}
          Save & Re-invite
        </button>
      </div>
    </div>
  );
}
